import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { fr, enUS, arSA } from 'date-fns/locale';

const locales = {
  fr,
  en: enUS,
  ar: arSA,
};

export function formatDate(date: string | Date | null | undefined, formatStr = 'PPP', locale = 'fr'): string {
  if (!date) return '-';
  try {
    const d = typeof date === 'string' ? parseISO(date) : date;
    if (isNaN(d.getTime())) return '-';
    return format(d, formatStr, { locale: locales[locale as keyof typeof locales] || fr });
  } catch {
    return '-';
  }
}

export function formatRelativeTime(date: string | Date | null | undefined, locale = 'fr'): string {
  if (!date) return '-';
  try {
    const d = typeof date === 'string' ? parseISO(date) : date;
    if (isNaN(d.getTime())) return '-';
    return formatDistanceToNow(d, {
      addSuffix: true,
      locale: locales[locale as keyof typeof locales] || fr
    });
  } catch {
    return '-';
  }
}

const CURRENCY_LOCALE_MAP: Record<string, string> = {
  EUR: 'fr-FR',
  USD: 'en-US',
  KMF: 'fr-KM',
};

// Map server currency formats to ISO currency codes
const CURRENCY_TO_ISO: Record<string, string> = {
  european_currency: 'EUR',
  american_currency: 'USD',
  comoros_currency: 'KMF',
  euro: 'EUR',
  EUR: 'EUR',
  USD: 'USD',
  KMF: 'KMF',
};

/**
 * Convert any currency format to ISO currency code
 */
export function toIsoCurrency(currency: string): string {
  return CURRENCY_TO_ISO[currency] || currency;
}

export function formatPrice(
  amount: number | string,
  currency = 'EUR',
  locale?: string
): string {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) return '-';

  const isoCurrency = toIsoCurrency(currency);
  const resolvedLocale = locale || CURRENCY_LOCALE_MAP[isoCurrency] || 'fr-FR';
  const fractionDigits = isoCurrency === 'KMF' ? 0 : 2;

  // For KMF, use 'code' display to show "KMF" instead of "CF"
  const currencyDisplay = isoCurrency === 'KMF' ? 'code' : 'symbol';

  return new Intl.NumberFormat(resolvedLocale, {
    style: 'currency',
    currency: isoCurrency,
    currencyDisplay,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(numAmount);
}

/**
 * Extract the correct amount from a server price object (comoros/euro/usd fields)
 * Used for products, orders, shipping rates, etc.
 */
export function getAmountFromPriceObject(
  priceObj: { comoros?: string | number; euro?: string | number; usd?: string | number } | null | undefined,
  currency: string,
  fallback = 0
): number {
  if (!priceObj) return fallback;
  let raw: string | number | undefined;
  switch (currency) {
    case 'USD': raw = priceObj.usd; break;
    case 'EUR': raw = priceObj.euro; break;
    case 'KMF':
    default: raw = priceObj.comoros; break;
  }
  const val = typeof raw === 'string' ? parseFloat(raw) : raw;
  return val != null && !isNaN(val) && val > 0 ? val : fallback;
}

/**
 * Pick the correct amount from multi-currency suffixed fields (xxxEur/xxxUsd/xxxKmf)
 * Used for advertising campaigns, ad wallet, ad transactions, etc.
 */
export function getAmountByCurrency(
  currency: string,
  amounts: { eur?: number; usd?: number; kmf?: number }
): number {
  switch (currency) {
    case 'USD': return amounts.usd ?? 0;
    case 'KMF': return amounts.kmf ?? 0;
    case 'EUR':
    default: return amounts.eur ?? 0;
  }
}

// Static exchange rates (1 EUR = X, fallback values)
const EXCHANGE_RATES: Record<string, number> = {
  EUR: 1,
  USD: 1.08,
  KMF: 492,
};

/**
 * Convert a single-value EUR amount to the selected currency.
 * Used for dashboard metrics, seller wallet, etc. where only one amount is available.
 */
export function convertFromEur(amount: number, currency: string): number {
  if (currency === 'EUR' || !amount) return amount;
  const rate = EXCHANGE_RATES[currency] || 1;
  return amount * rate;
}

export function formatNumber(num: number, locale = 'fr-FR'): string {
  return new Intl.NumberFormat(locale).format(num);
}

export function formatPercentage(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}
